import { Alert, AlertTitle, Button, Card, CardContent, Skeleton } from "@colorglo/ui/components";
import { FeedbackError, iconSize } from "@colorglo/ui/icons";
import { useBlocker } from "@tanstack/react-router";
import { useT } from "../../lib/locale.ts";

export function SettingsSection({
  heading,
  body,
  children,
}: {
  readonly heading: string;
  readonly body?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-h2">{heading}</h2>
        {body !== undefined && (
          <p className="max-w-prose text-body-sm text-muted-foreground">{body}</p>
        )}
      </div>
      {children}
    </section>
  );
}

export function SettingsCard({ children }: { readonly children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-5">{children}</CardContent>
    </Card>
  );
}

export function SettingsLoading() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

export function SettingsError({ onRetry }: { readonly onRetry: () => void }) {
  const t = useT();
  return (
    <Alert variant="destructive">
      <FeedbackError {...iconSize.control} aria-hidden="true" />
      <AlertTitle>{t("settings.load_error")}</AlertTitle>
      <Button variant="outline" className="mt-3" onClick={onRetry}>
        {t("settings.retry")}
      </Button>
    </Alert>
  );
}

/**
 * The save bar every screen ends with, plus the guard that stops a navigation away from
 * unsaved work. `enableBeforeUnload` covers the reload and the closed tab.
 */
export function SaveBar({
  dirty,
  saving,
  saved,
  invalid = false,
  onSave,
  onDiscard,
}: {
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly saved: boolean;
  readonly invalid?: boolean;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
}) {
  const t = useT();

  useBlocker({
    disabled: !dirty,
    enableBeforeUnload: () => dirty,
    shouldBlockFn: () => !window.confirm(t("settings.leave_warning")),
  });

  return (
    <div className="sticky bottom-0 -mx-4 flex items-center gap-3 px-4 py-3 md:-mx-6 md:px-6">
      <Button onClick={onSave} disabled={!dirty || saving || invalid}>
        {saving ? t("settings.saving") : t("settings.save")}
      </Button>
      {dirty && (
        <Button variant="ghost" onClick={onDiscard} disabled={saving}>
          {t("settings.discard")}
        </Button>
      )}
      <p role="status" aria-live="polite" className="text-body-sm text-muted-foreground">
        {dirty ? t("settings.unsaved") : saved ? t("settings.saved") : ""}
      </p>
    </div>
  );
}
